import React, { useState, useMemo, useEffect } from 'react';
import styles from './TrainingServiceSelector.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import { TrainingService } from 'model/Service';
import { Spinner } from 'components/UI';
import { faClock, faHouse } from '@fortawesome/pro-regular-svg-icons';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import GenericSelect, {
    GenericSelectOption
} from 'components/CreateBooking/Transportation/UI/GenericSelect/GenericSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { fetchTrainingServicesThunk } from 'actions/services/ServicesActions';
import { useApiClientWrapper } from 'hooks';

interface Props {
    loading?: boolean;
    value?: TrainingService;
    onChange?: (value: TrainingService) => void;
    className?: string;
    petId: number;
    servicesUsed?: Array<number>;
    inHomeService?: boolean;
}

export const TrainingServiceSelector: React.FC<Props> = ({
    value,
    onChange,
    className,
    loading,
    petId,
    servicesUsed,
    inHomeService
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const services = useSelector<RootState, TrainingService[] | undefined>(state => state.services.trainingServices);

    const [selected, setSelected] = useState<TrainingService | undefined>(value);

    useEffect(() => {
        setSelected(value);
    }, [value]);

    useEffect(() => {
        dispatch(fetchTrainingServicesThunk(petId, apiClientWrapper.marketplaceId));
    }, [petId, dispatch, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (selected?.serviceId && services) {
            const updatedService = services.find(s => s.serviceId === selected.serviceId);
            if (updatedService) {
                setSelected(updatedService);
            }
        }
    }, [services, selected?.serviceId]);

    const inHomeTag = (
        <div className={styles.inHomeServiceTag}>
            <FontAwesomeIcon
                icon={faHouse}
                className={styles.icon}
            />
            <label>Home Service</label>
        </div>
    );

    const formatDuration = (minutes: number | undefined): string => {
        if (!minutes) return '0 min';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        if (hours === 0) {
            return `${mins} min`;
        }
        if (mins === 0) {
            return `${hours} h`;
        }
        return `${hours} h ${mins} min`;
    };

    const options: GenericSelectOption[] = useMemo(() => {
        if (!services) return [];

        const filteredServices = services.filter(s => {
            if (!servicesUsed || servicesUsed.length === 0) return true;
            if (value?.serviceId === s.serviceId) return true;
            return !servicesUsed.includes(s.serviceId);
        });

        return filteredServices.map(s => {
            const priceAndDuration = (
                <div className={styles.sectionDuration}>
                    {s.inHomeService && (
                        <div className={styles.inHomeServiceTag}>
                            <FontAwesomeIcon
                                icon={faHouse}
                                className={styles.icon}
                            />
                            <label>Home Service</label>
                        </div>
                    )}
                    <div className={styles.serviceItemContainer}>
                        <div className={styles.durationContainer}>
                            <FontAwesomeIcon
                                icon={faClock}
                                className={clsx(styles.item)}
                            />
                            <span className={clsx(styles.item, styles.spacing)}>{formatDuration(s.duration)}</span>
                        </div>
                    </div>
                    <div className={styles.serviceItemContainer}>
                        <span className={clsx(styles.item, styles.bold, styles.spacing)}>${s.price?.toFixed(2)}</span>
                    </div>
                </div>
            );

            return {
                id: s.serviceId,
                label: s.serviceName,
                color: s.color,
                rightLabel: priceAndDuration
            };
        });
    }, [services, servicesUsed, value?.serviceId]);

    const handleSelect = (opt: GenericSelectOption) => {
        const selectedService = services?.find((op: TrainingService) => op.serviceId === opt.id);
        setSelected(selectedService);
        onChange?.(selectedService!);
    };

    const selectedOption = useMemo(() => {
        const option = options.find(o => o.id === selected?.serviceId);
        if (!option) return null;

        const isInHomeService =
            inHomeService !== undefined && selected?.serviceId === option.id ? inHomeService : selected?.inHomeService;
        return {
            ...option,
            rightLabel: isInHomeService ? <div className={styles.rightLabel}>{inHomeTag}</div> : undefined
        };
    }, [options, selected?.serviceId, selected?.inHomeService, inHomeService]);

    const emptyServicesComponent = (
        <div id="booking_div_no_services_alert">
            <label className={styles.title}>Services</label>
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <p className={styles.alertText}>No services available for this pet</p>
            </AlertNewBooking>
        </div>
    );

    return (
        <div className={clsx(styles.container, className)}>
            {loading && <Spinner />}
            {!loading && (!services || services?.length === 0 || options.length === 0) && emptyServicesComponent}
            {!loading && services && services?.length > 0 && options.length > 0 && (
                <>
                    <label className={styles.title}>Services</label>
                    <GenericSelect
                        placeholder="Select Service"
                        selected={selectedOption}
                        options={options}
                        onSelect={handleSelect}
                        showRightLabelOnSelected={true}
                        className={styles.select}
                    />
                </>
            )}
        </div>
    );
};

export default TrainingServiceSelector;
